import { apiGet, apiSend } from '../../session/client';

/**
 * Everything the Account page reads and writes, and nothing it invents.
 *
 * Each call is an existing platform route — the same ones the Android app's
 * account screens use (designs 14.x and 25.11). The person is always the
 * signed-in one: every route here is `/me`-shaped, so there is no id in any
 * path or body for a bug to point at somebody else.
 */

export interface ContactPoint {
  readonly contactPointId: string;
  readonly channel: 'email' | 'sms' | 'phone';
  readonly value: string;
  readonly isPrimary: boolean;
  readonly verifiedAt?: string;
  /** `identity` is the sign-in email, which the platform will not change here. */
  readonly source: 'identity' | 'profile';
}

export interface Address {
  readonly addressId: string;
  readonly label?: string;
  readonly recipientName?: string;
  readonly line1: string;
  readonly line2?: string;
  readonly city: string;
  readonly region?: string;
  readonly postcode: string;
  readonly countryCode: string;
  readonly deliveryInstructions?: string;
  readonly isDefault: boolean;
}

export interface Profile {
  readonly personId: string;
  readonly displayName?: string;
  /** YYYY-MM-DD. */
  readonly dateOfBirth?: string;
  readonly contacts: readonly ContactPoint[];
  readonly addresses: readonly Address[];
}

export interface InboxItem {
  readonly id: string;
  readonly title: string;
  readonly body: string;
  readonly createdAt: string;
  readonly readAt?: string;
}

export interface Inbox {
  readonly notifications: readonly InboxItem[];
  readonly unread: number;
}

export interface ConsentRecord {
  readonly purpose: string;
  readonly channel?: string | null;
  readonly isActive: boolean;
}

export interface MfaFactor {
  readonly factorId: string;
  readonly kind: 'totp' | 'passkey' | 'sms';
  readonly status: 'pending' | 'confirmed' | 'revoked';
}

export interface Wallet {
  readonly availableUnits?: number;
}

export const getProfile = (): Promise<Profile> => apiGet<Profile>('/person/me');

export const updateProfile = (body: { displayName?: string; dateOfBirth?: string }): Promise<Profile> =>
  apiSend<Profile>('PATCH', '/person/me', body);

export const getAddresses = async (): Promise<readonly Address[]> =>
  (await apiGet<{ addresses: Address[] }>('/person/me/addresses')).addresses;

export interface AddressInput {
  readonly label?: string;
  readonly recipientName?: string;
  readonly line1: string;
  readonly line2?: string;
  readonly city: string;
  readonly region?: string;
  readonly postcode: string;
  readonly countryCode: string;
  readonly deliveryInstructions?: string;
}

export const addAddress = (body: AddressInput): Promise<Address> =>
  apiSend<Address>('POST', '/person/me/addresses', body);

export const editAddress = (id: string, body: AddressInput): Promise<Address> =>
  apiSend<Address>('PATCH', `/person/me/addresses/${encodeURIComponent(id)}`, body);

export const makeDefaultAddress = (id: string): Promise<Address> =>
  apiSend<Address>('POST', `/person/me/addresses/${encodeURIComponent(id)}/default`);

export const removeAddress = (id: string): Promise<unknown> =>
  apiSend('DELETE', `/person/me/addresses/${encodeURIComponent(id)}`);

export const addContact = (channel: 'sms', value: string): Promise<ContactPoint> =>
  apiSend<ContactPoint>('POST', '/person/me/contacts', { channel, value });

export const removeContact = (id: string): Promise<unknown> =>
  apiSend('DELETE', `/person/me/contacts/${encodeURIComponent(id)}`);

export const getInbox = (): Promise<Inbox> => apiGet<Inbox>('/consumer/notifications');

export const readOne = (id: string): Promise<unknown> =>
  apiSend('POST', `/consumer/notifications/${encodeURIComponent(id)}/read`);

export const readAll = (): Promise<unknown> => apiSend('POST', '/consumer/notifications/read-all');

export const getConsents = async (): Promise<readonly ConsentRecord[]> =>
  (await apiGet<{ consents: ConsentRecord[] }>('/consent/me')).consents;

/**
 * The wording this page shows next to each consent switch.
 *
 * Sent with every grant so the record says what the person was reading when
 * they agreed. Change it whenever the wording of those rows changes.
 */
export const CONSENT_WORDING_VERSION = 'web-account-v1';

export const grantConsent = (purpose: 'marketing' | 'analytics', channel?: string): Promise<unknown> =>
  apiSend('POST', '/consent/me/grant', {
    purpose,
    ...(channel !== undefined ? { channel } : {}),
    dataCategory: 'PERSONAL',
    wordingVersion: CONSENT_WORDING_VERSION,
  });

export const withdrawConsent = (purpose: 'marketing' | 'analytics', channel?: string): Promise<unknown> =>
  apiSend('POST', '/consent/me/withdraw', { purpose, ...(channel !== undefined ? { channel } : {}) });

export const getFactors = async (): Promise<readonly MfaFactor[]> =>
  (await apiGet<{ factors: MfaFactor[] }>('/identity/mfa/factors')).factors;

export const changePassword = (currentPassword: string, newPassword: string): Promise<unknown> =>
  apiSend(
    'POST',
    '/identity/change-password',
    { currentPassword, newPassword },
    // A 401 here is usually the wrong current password: shown once, never retried.
    { retryUnauthorized: false },
  );

export const logoutOthers = (): Promise<{ revoked: true; count: number }> =>
  apiSend('POST', '/identity/logout-others');

export const getWallet = (): Promise<Wallet> => apiGet<Wallet>('/consumer/penny');

/**
 * Orders on this device, and (signed in) the account's orders from any device
 * (stage 4). The route answers at most 50; 50 back means "at least 50", and the
 * page says so rather than claiming exactly 50.
 */
export const ORDERS_PAGE = 50;
export const getDeviceOrderCount = async (): Promise<number> =>
  (await apiGet<unknown[]>(`/consumer/orders?limit=${ORDERS_PAGE}`)).length;

interface BookingsPage {
  readonly items: readonly { status: string; endsAt: string }[];
  readonly pageInfo: { nextCursor?: string; hasMore: boolean };
}

/**
 * Upcoming appointments: pending or confirmed, not yet over.
 *
 * Every page is read (up to a ceiling), because counting only the first page
 * would quietly under-report — the capped-list trap. Past the ceiling the
 * count is reported as a floor.
 */
export async function getUpcomingBookings(now = Date.now()): Promise<{ count: number; atLeast: boolean }> {
  let cursor: string | undefined;
  let count = 0;
  for (let page = 0; page < 10; page += 1) {
    const result = await apiGet<BookingsPage>(
      `/me/bookings?limit=100${cursor !== undefined ? `&cursor=${encodeURIComponent(cursor)}` : ''}`,
    );
    count += result.items.filter(
      (b) => (b.status === 'PENDING' || b.status === 'CONFIRMED') && new Date(b.endsAt).getTime() > now,
    ).length;
    if (!result.pageInfo.hasMore || result.pageInfo.nextCursor === undefined)
      return { count, atLeast: false };
    cursor = result.pageInfo.nextCursor;
  }
  return { count, atLeast: true };
}
